import React, {useEffect, useRef, useState} from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Modal,
  CircularProgress,
  Divider,
  List, ListItem, ListItemText,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import orderItemService from "../services/orderItemService";
import GetSearchAddress from "../utils/GetSearchAddress";

const formatCurrency = (n) => {
  if (n == null) return "0"; // n이 null 또는 undefined인 경우 "0" 반환
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
/* ------------------------- 지난주소 불러오기 다이얼로그 ------------------------- */
const AddressSelectModal = ({ open, onClose, addresses, onSelect }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>지난 배송주소 목록</DialogTitle>
      <List>
        <Divider /> {/* 처음에 실선 추가 */}
        {addresses.map((addr, index) => (
          <React.Fragment key={addr.id || index}>
            <ListItem
              button
              onClick={() => {
                onSelect(addr);
                onClose();
              }}
            >
              <ListItemText
                primary={`${addr.address} ${addr.address_detail}`}
                secondary={
                  <>
                    <div>수령인: {addr.receiver_name}, 전화번호: {addr.phone_number}</div>
                    <div>요청사항: {addr.delivery_comment || "없음"}</div>
                  </>
                }
              />
            </ListItem>
            <Divider /> {/* 각 항목 사이에 실선 추가 */}
          </React.Fragment>
        ))}
      </List>
      <Button onClick={onClose} color="secondary">
        닫기
      </Button>
    </Dialog>
  );
};

/* ------------------------- 상품 검색 모달 ------------------------- */
const ProductSearchModal = ({ open, onClose, onSelect }) => {
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const inputRef = useRef(null); // 검색 필드 참조 생성

  useEffect(() => {
    if (open) {
      setKeyword(""); // 검색 필드 초기화
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus(); // 검색 필드에 포커스
        }
      }, 0); // 약간의 지연 후 포커스 설정
    }
  }, [open]);

  const handleSearch = async () => {
    setLoading(true);
    setResults([]); // 이전 검색 결과 초기화
    try {
      const { data } = await orderItemService.fetchItemsName(keyword.trim());
      setResults(data || []);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        alert("검색 상품이 없습니다.");
      } else {
        alert("상품 검색 실패");
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          backgroundColor: "white",
          p: 3,
          borderRadius: 2,
          width: 500,
          maxWidth: "90%",
          mx: "auto",
          mt: "10vh",
          maxHeight: "80vh", // 최대 높이 설정
          overflow: "auto",  // 스크롤 활성화
        }}
      >
        <Typography variant="h6" mb={2}>상품 검색</Typography>
        <TextField
          fullWidth
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="상품명"
          inputRef={inputRef}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
        />
        {loading ? (
          <CircularProgress />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>상품명</TableCell>
                <TableCell>공급가</TableCell>
                <TableCell>배송비</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{formatCurrency(r.sale_price)}</TableCell>
                  <TableCell>{formatCurrency(r.delivery_price)}</TableCell>
                  <TableCell align="right">
                    <Button onClick={() => { onSelect(r); onClose(); }}>선택</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Modal>
  );
};

/* --------------------------- 메인 컴포넌트 --------------------------- */
const OrderItem = () => {
  const navigate = useNavigate();

  /* 회원 조회 */
  const [ordererName, setOrdererName] = useState("");
  const [ordererId, setOrdererId] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [deliveryComment, setDeliveryComment] = useState("");
  const [addressList, setAddressList] = useState([]); // 지난 주소 리스트
  const [openAddrModal, setOpenAddrModal] = useState(false); // 주소 선택 모달 열림 상태

  const [isModalVisible, setIsModalVisible] = useState(false);


  // 주소 선택 처리
  const onChangeFullAddress = (fullAddress, zipcode) => {
    setZip(zipcode);
    setAddr(fullAddress);
    closeAddressModal();
  };

  const fetchOrdererByPhone = async (p) => {
    if (!p) return;
    try {
      const res = await orderItemService.fetchUserInfo(p);
      const d = res.data;
      if (d?.user_id) {
        setOrdererName(d.name);
        setOrdererId(d.user_id);
      } else alert("등록된 회원이 아닙니다.");
    } catch (e) {
      console.error(e);
      alert("조회 실패");
    }
  };

  const [depositorName, setDepositorName] = useState("");
  const [depositDate, setDepositDate] = useState("");
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD 형식
    setDepositDate(today);
  }, []);
  useEffect(() => {
    if (ordererName) {
      setDepositorName(ordererName); // 회원명으로 입금자명 설정
      setReceiverName(ordererName); // 수령인도 회원명으로 설정
      setReceiverPhone(phoneNumber);
    }
  }, [ordererName, phoneNumber]);
  /* 수령인 */
  const [receiverName, setReceiverName] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");

  /* 배송 */
  const [deliveryMethod, setDeliveryMethod] = useState("택배");
  const [zip, setZip] = useState("");
  const [addr, setAddr] = useState("");
  const [addrDet, setAddrDet] = useState("");
  const openAddressModal = () => setIsModalVisible(true);
  const closeAddressModal = () => setIsModalVisible(false);
  // const handleAddrSelect = (z, a) => { setZip(z); setAddr(a); };
  // 모달에서 선택된 주소 처리
  const handleAddressSelect = (selectedAddr) => {
    setZip(selectedAddr.zipcode);
    setAddr(selectedAddr.address);
    setAddrDet(selectedAddr.address_detail);
    setReceiverName(selectedAddr.receiver_name);
    setReceiverPhone(selectedAddr.phone_number);
    setDeliveryComment(selectedAddr.delivery_comment);
  };
  const handleLoadLastAddr = async () => {
    if (!ordererId) {
      alert("주문자 정보를 입력하세요.");
      return;
    }
    try {
      const response = await orderItemService.fetchLastAddress(ordererId);
      const addresses = response.data;

      if (addresses.length === 1) {
        // 한 건일 경우 바로 적용
        const { zipcode, address, address_detail, receiver_name, phone_number, delivery_comment } = addresses[0];
        setZip(zipcode);
        setAddr(address);
        setAddrDet(address_detail);
        setReceiverName(receiver_name);
        setReceiverPhone(phone_number);
        setDeliveryComment(delivery_comment);
      } else if (addresses.length > 1) {
        // 여러 건일 경우 리스트 보여주기
        setAddressList(addresses);
        setOpenAddrModal(true);
      } else {
        alert("지난 배송주소가 없습니다.");
      }
    } catch (error) {
      console.error("지난 배송주소 불러오기 실패:", error);
      alert("지난 주소를 불러오는 중 오류가 발생했습니다.");
    }
  };

  /* 상품 */
  const [products, setProducts] = useState([]);
  const [openProdModal, setOpenProdModal] = useState(false);
  const handleProductSelect = (r) => {
    if (products.some(p => p.item_id === r.item_id)) { alert("이미 추가됨"); return; }
    const p = { item_id: r.item_id, name: r.name, quantity: 1, unit_price: r.sale_price, sale_price: r.sale_price, delivery_price: deliveryMethod === "직접수령" ? 0 : r.delivery_price, totalPrice: r.sale_price + (deliveryMethod === "직접수령" ? 0 : r.delivery_price) };
    setProducts(prev => [...prev, p]);
  };
  const handleDeliveryMethodChange = (method) => {
    setDeliveryMethod(method);
    if (method === "직접수령") {
      setProducts(prev =>
        prev.map(p => ({
          ...p,
          delivery_price: 0,
          totalPrice: p.sale_price, // 배송비 0으로 설정 후 totalPrice 재계산
        }))
      );
    }
  };
  const handleProductUpdate = (item_id, field, value) => {
    setProducts(prev =>
      prev.map(p =>
        p.item_id !== item_id
          ? p
          : {
              ...p,
              [field]: value,
              sale_price: field === "quantity" ? p.unit_price * value : field === "sale_price" ? value : p.sale_price,
              // delivery_price: field === "delivery_price" && value === 0 ? 0 : p.delivery_price,
              totalPrice:
                field === "quantity"
                  ? p.unit_price * value + p.delivery_price
                  : field === "sale_price" || field === "delivery_price"
                  ? (field === "sale_price" ? value : p.sale_price) + (field === "delivery_price" ? value : p.delivery_price)
                  : p.totalPrice,
            }
      )
    );
  };
  const calcTotal = () => {
    const totalPayment = products.reduce((sum, p) => sum + p.totalPrice, 0);
    return totalPayment;
  };

  /* 결제 */
  const [paymentMethod, setPaymentMethod] = useState("무통장입금");

  /* 저장 */
  const handleSave = async () => {
    if (!ordererId) { alert("주문자 정보 입력"); return; }
    if (!products.length) { alert("주문상품 선택"); return; }
    if (!receiverName || !receiverPhone) { alert("수령인 정보 입력"); return; }
    const orderData = new FormData();
    orderData.append("user_id", ordererId);
    orderData.append("phone_no", phoneNumber);
    orderData.append("receiver_name", receiverName);
    orderData.append("receiver_phone", receiverPhone);
    orderData.append("delivery_method", deliveryMethod);
    if (deliveryMethod === "택배") { orderData.append("zip_code", zip); orderData.append("address", addr); orderData.append("address_detail", addrDet); orderData.append("delivery_comment", deliveryComment);}
    orderData.append("logistics_center_id","674776c4-ac11-11ef-97ed-42010a400003"); // 대구수성센타
    if (paymentMethod === "무통장입금") {
      orderData.append("deposit_name", depositorName);
      orderData.append("deposit_date", depositDate);
    }
    orderData.append("payment_method", paymentMethod);
    // orderData.append("items", JSON.stringify(products));
      // items를 리스트로 추가
    products.forEach((product, index) => {
      orderData.append(`items[${index}][item_id]`, product.item_id);
      orderData.append(`items[${index}][name]`, product.name);
      orderData.append(`items[${index}][quantity]`, product.quantity);
      orderData.append(`items[${index}][unit_price]`, product.unit_price);
      orderData.append(`items[${index}][sale_price]`, product.sale_price);
      orderData.append(`items[${index}][delivery_price]`, product.delivery_price);
      orderData.append(`items[${index}][total_price]`, product.totalPrice);
    });
    // FormData 내용을 콘솔에 출력
    for (const [key, value] of orderData.entries()) {
      console.log(`${key}: ${value}`);
    }
    try { await orderItemService.addOrderItem(orderData); alert("현장 주문등록 완료"); window.location.reload();}
    catch (e) { console.error(e); alert("현장 주문등록 실패"); }
  };

  return (
    <Container maxWidth={false} sx={{ width: "70%", pb: 8 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={5} mb={4}>
        <Box display="flex" alignItems="center"><IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}><ArrowBack /></IconButton><Typography variant="h4">현장 주문 등록</Typography></Box>
        <Button variant="contained" onClick={handleSave}>저장하기</Button>
      </Box>

      {/* 주문자 */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>주문자(회원) 정보</Typography><Divider sx={{ mb: 2 }} />
        <Stack spacing={3} direction={{ xs: "column", sm: "row" }}>
          <TextField
            label="회원님 전화번호를 입력하고 엔터를 누르세요"
            value={phoneNumber}
            fullWidth
            onChange={(e) => {
              const onlyNumbers = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 허용
              setPhoneNumber(onlyNumbers);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'Tab') {
                fetchOrdererByPhone(phoneNumber.trim());
              }
            }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            sx={{ flex: 1 }}
          />
          <TextField label="전화번호 입력하면 회원명이 보여집니다" value={ordererName} InputProps={{ readOnly: true, sx: { backgroundColor: "#f5f5f5" }, }} sx={{ flex: 1 }} />
        </Stack>
      </Paper>

      {/* 배송 */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>배송 정보</Typography><Divider sx={{ mb: 2 }} />
        <FormControl component="fieldset" sx={{ mb: 3 }}><FormLabel>배송 방법</FormLabel>
          <RadioGroup row value={deliveryMethod} onChange={(e) => handleDeliveryMethodChange(e.target.value)}>
            <FormControlLabel value="택배" control={<Radio />} label="택배" />
            <FormControlLabel value="직접수령" control={<Radio />} label="직접수령" />
          </RadioGroup>
        </FormControl>
        <Typography variant="subtitle1" fontWeight={600}>수령인 정보</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ my: 2 }}>
          <TextField label="수령인" value={receiverName} onChange={(e) => setReceiverName(e.target.value)} fullWidth />
          <TextField
            label="전화번호"
            value={receiverPhone}
            onChange={(e) => {
              const onlyNumbers = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 허용
              setReceiverPhone(onlyNumbers);
            }}
            fullWidth
            inputProps={{
              inputMode: 'tel',
              pattern: '[0-9]*',
            }}
          />
        </Stack>
        {deliveryMethod === "택배" && (
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>물품 수령 주소</Typography>
            <Stack direction="row" spacing={2}>
              <TextField label="우편번호" value={zip} sx={{ width: 160 }} InputProps={{ readOnly: true }} />
              <Button variant="outlined" onClick={openAddressModal}>주소 검색</Button>
              {isModalVisible && (
                <Dialog open={isModalVisible} onClose={closeAddressModal} maxWidth="sm" fullWidth>
                  <DialogTitle>주소 검색</DialogTitle>
                  <DialogContent dividers>
                    <GetSearchAddress
                      onSelect={onChangeFullAddress}
                      onCloseModal={closeAddressModal}
                    />
                  </DialogContent>
                </Dialog>
              )}
              <Button variant="contained" onClick={handleLoadLastAddr}>지난 배송주소</Button>
            </Stack>
            <TextField label="기본 주소" value={addr} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="상세 주소" value={addrDet} fullWidth onChange={(e) => setAddrDet(e.target.value)} />
            <TextField
              label="배송 요청사항"
              value={deliveryComment}
              onChange={(e) => setDeliveryComment(e.target.value)}
              fullWidth
              placeholder="예: 문 앞에 놓아주세요."
            />
          </Stack>
        )}
      </Paper>
      {/* AddressSelectModal */}
      <AddressSelectModal
        open={openAddrModal}
        onClose={() => setOpenAddrModal(false)}
        addresses={addressList}
        onSelect={handleAddressSelect}
      />

      {/* 상품 선택 */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}><Typography variant="h6">상품 선택</Typography><Button variant="contained" onClick={() => setOpenProdModal(true)}>상품 추가</Button></Box>
        <Table
            size="small"
            // sx={{
            //   tableLayout: "fixed", // 테이블 레이아웃 고정
            //   width: "100%", // 테이블 전체 가로 길이 설정
            // }}
        >
          <TableHead>
            <TableRow>
              <TableCell align="left">
                상품명
              </TableCell>
              <TableCell align="center">수량</TableCell>
              <TableCell align="center">단가</TableCell>
              <TableCell align="center">상품금액</TableCell>
              <TableCell align="center">배송비</TableCell>
              <TableCell align="center">결제금액</TableCell>
              <TableCell align="right">삭제</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.length ? (
              products.map(p => (
                <TableRow key={p.item_id}>
                  <TableCell align="left">
                    {p.name}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={p.quantity}
                      inputProps={{ min: 1, style: { width: 60 } }}
                      onChange={(e) => handleProductUpdate(p.item_id, "quantity", parseInt(e.target.value) || 1)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={p.unit_price}
                      inputProps={{ style: { width: 100, backgroundColor: "#f5f5f5" }, readOnly: true }}
                      onChange={(e) => handleProductUpdate(p.item_id, "sale_price", parseInt(e.target.value) || 0)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={p.sale_price}
                      inputProps={{ style: { width: 100 } }}
                      onFocus={(e) => e.target.select()} // 포커스 시 전체 선택
                      onChange={(e) => handleProductUpdate(p.item_id, "sale_price", parseInt(e.target.value) || 0)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={deliveryMethod === "직접수령" ? 0 : p.delivery_price}
                      disabled={deliveryMethod === "직접수령"}
                      inputProps={{ style: { width: 100 } }}
                      onFocus={(e) => e.target.select()} // 포커스 시 전체 선택
                      onChange={(e) => handleProductUpdate(p.item_id, "delivery_price", parseInt(e.target.value) || 0)}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: "inherit" }}>{formatCurrency(p.totalPrice)}원</TableCell>
                  <TableCell align="right">
                    <Button color="error" sx={{ fontSize: "inherit", textTransform: "none" }} onClick={() => setProducts(prev => prev.filter(i => i.item_id !== p.item_id))}>
                      삭제
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <>
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ color: "gray", borderBottom: "1px solid rgba(224, 224, 224, 1)" }}>
                    상품을 추가하세요
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* 결제 방법 */}
      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>결제 방법</Typography>
        <RadioGroup row value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}><FormControlLabel value="무통장입금" control={<Radio />} label="무통장입금" /><FormControlLabel value="카드결제" control={<Radio />} label="카드결제" /></RadioGroup>
        {paymentMethod === "무통장입금" && (

          <Stack spacing={2} mt={2}>
            <TextField
              label="입금자명"
              value={depositorName}
              onChange={(e) => setDepositorName(e.target.value)}
              fullWidth
            />
            <TextField
              label="입금일자"
              type="date"
              value={depositDate}
              onChange={(e) => setDepositDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        )}
      </Paper>

      {/* 총 결제 */}
      <Paper sx={{ p: 4, bgcolor: "#e8f0ff", border: "2px solid #90b2ff" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center"><Typography variant="h5" fontWeight={700}>총 결제금액</Typography><Typography variant="h4" fontWeight={700} color="primary">{formatCurrency(calcTotal())}원</Typography></Box>
      </Paper>

      {/* Dialog / Modal */}
      {/*<AddressSearchDialog open={openAddressModal} onClose={() => setOpenAddrModal(false)} onSelect={handleAddressSelect} />*/}
      <ProductSearchModal open={openProdModal} onClose={() => setOpenProdModal(false)} onSelect={handleProductSelect} />
    </Container>
  );
};

export default OrderItem;